import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Minus, Clock, ArrowRightLeft } from 'lucide-react';
import { btnVoltar, aviso, tabela, thEscuro, thEscuroNum, td, tdNum, COR_MODULO } from './escala-ui';
import { HORAS } from './escala-grade-utils';

// Comparação de duas versões da mesma loja.
// "Quando mudar tem que ser rastreável e apontar as diferenças" (Rodrigo, 14/09)
// — esta tela é o que transforma o versionamento em informação.
//
// ⚠️ Só aparece o que MUDOU. Listar os grupos iguais afogaria a mudança, que é
//    o produto aqui. O contador de inalterados fica no resumo.

const API = '/api/rh/dimensionamento-escala';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const ICONE = {
    adicionado: <Plus size={14} />,
    removido: <Minus size={14} />,
    'qtd-alterada': <ArrowRightLeft size={14} />,
    'break-movido': <Clock size={14} />,
};
const COR_TIPO = {
    adicionado: { bg: '#dcfce7', color: '#166534' },
    removido: { bg: '#fee2e2', color: '#991b1b' },
    'qtd-alterada': { bg: '#dbeafe', color: '#1d4ed8' },
    'break-movido': { bg: '#fef3c7', color: '#92400e' },
};

const RHDimensionamentoEscalaComparar = ({ a, b, onSair }) => {
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/versoes/${a}/diff/${b}`, { headers: auth() });
                const d = await r.json();
                if (!r.ok) { setErro(d?.message || 'Erro ao comparar.'); return; }
                setDados(d);
            } catch (e) { setErro('Erro de rede ao comparar.'); }
        })();
    }, [a, b]);

    if (erro) {
        return (
            <div>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <div style={{ ...aviso('#fee2e2', '#991b1b'), marginTop: 16 }}>{erro}</div>
            </div>
        );
    }
    if (!dados) {
        return (
            <div>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <p style={{ color: '#64748b', marginTop: 16 }}>Carregando…</p>
            </div>
        );
    }

    const { resumo, grupos, curvas, antes, depois, semMudanca } = dados;
    const delta = (v, invertido) => {
        if (v === 0) return <span style={{ color: '#64748b' }}>=</span>;
        const bom = invertido ? v < 0 : v > 0;
        return <b style={{ color: bom ? '#b91c1c' : '#1d4ed8' }}>{v > 0 ? `+${v}` : v}</b>;
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <div style={{ fontWeight: 800, fontSize: 16 }}>{antes.LOJA} — comparação</div>
            </div>

            <div style={aviso('#f1f5f9', '#334155')}>
                <b>{antes.NOME_VERSAO}</b> ({antes.STATUS}, {new Date(antes.DATA_CRIACAO).toLocaleDateString('pt-BR')})
                {'  →  '}
                <b>{depois.NOME_VERSAO}</b> ({depois.STATUS}, {new Date(depois.DATA_CRIACAO).toLocaleDateString('pt-BR')})
            </div>

            {/* Resumo */}
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={tabela}>
                    <thead>
                        <tr style={{ background: '#0f172a' }}>
                            <th style={thEscuro}></th>
                            <th style={thEscuroNum}>Antes</th>
                            <th style={thEscuroNum}>Depois</th>
                            <th style={thEscuroNum}>Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={td}>Horas-pessoa (os dois períodos)</td>
                            <td style={tdNum}>{resumo.horasAntes}</td>
                            <td style={tdNum}>{resumo.horasDepois}</td>
                            <td style={tdNum}>{delta(resumo.deltaHoras)}</td>
                        </tr>
                        <tr>
                            <td style={td}>Posições no dia <span style={{ color: '#94a3b8' }}>(o que a grade desenha)</span></td>
                            <td style={tdNum}>{resumo.posicoesAntes}</td>
                            <td style={tdNum}>{resumo.posicoesDepois}</td>
                            <td style={tdNum}>{delta(resumo.deltaPosicoes)}</td>
                        </tr>
                        <tr>
                            <td style={{ ...td, fontWeight: 700 }}>
                                Efetivo <span style={{ color: '#94a3b8', fontWeight: 400 }}>(o que a folha precisa ter — 12x36 conta dobrado)</span>
                            </td>
                            <td style={{ ...tdNum, fontWeight: 700 }}>{resumo.efetivoAntes}</td>
                            <td style={{ ...tdNum, fontWeight: 700 }}>{resumo.efetivoDepois}</td>
                            <td style={tdNum}>{delta(resumo.deltaEfetivo)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* O que mudou */}
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                {grupos.length === 0 ? 'Nada mudou entre as duas versões.' : `${grupos.length} mudança(s)`}
                <span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>
                    {' '}· {semMudanca} grupo(s) sem alteração
                </span>
            </h4>

            {grupos.length > 0 && (
                <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                    <table style={tabela}>
                        <thead>
                            <tr style={{ background: '#0f172a' }}>
                                <th style={thEscuro}>Período</th>
                                <th style={thEscuro}>Cargo</th>
                                <th style={thEscuro}>Escala</th>
                                <th style={thEscuro}>O que mudou</th>
                            </tr>
                        </thead>
                        <tbody>
                            {grupos.map((g, i) => {
                                const c = COR_TIPO[g.tipo] || { bg: '#f1f5f9', color: '#475569' };
                                return (
                                    <tr key={i}>
                                        <td style={td}>{g.periodo}</td>
                                        <td style={{ ...td, fontWeight: 700 }}>{g.cargo}</td>
                                        <td style={td}>{g.turno}</td>
                                        <td style={td}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                background: c.bg, color: c.color, padding: '3px 8px',
                                                borderRadius: 999, fontSize: 11, fontWeight: 700, marginRight: 8,
                                            }}>
                                                {ICONE[g.tipo]} {g.tipo}
                                            </span>
                                            {g.texto}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* As curvas sobrepostas */}
            {['SEG-QUI', 'SEX-DOM'].map((p) => {
                const c = curvas[p];
                const max = Math.max(...c.antes, ...c.depois, 1);
                return (
                    <div key={p} style={{ marginBottom: 18 }}>
                        <h4 style={{ margin: '0 0 6px', fontSize: 13 }}>
                            {p} <span style={{ fontWeight: 400, color: '#64748b' }}>— {c.horasAntes}h → {c.horasDepois}h</span>
                        </h4>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ borderCollapse: 'collapse', fontSize: 11, background: '#fff', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={thC}>hora</th>
                                        {HORAS.map((h) => <th key={h} style={thC}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td style={{ ...tdC, textAlign: 'left', color: '#64748b' }}>antes</td>
                                        {c.antes.map((v, i) => <td key={i} style={tdC}>{v}</td>)}</tr>
                                    <tr><td style={{ ...tdC, textAlign: 'left', fontWeight: 700 }}>depois</td>
                                        {c.depois.map((v, i) => <td key={i} style={{ ...tdC, fontWeight: 700 }}>{v}</td>)}</tr>
                                    <tr><td style={{ ...tdC, textAlign: 'left', color: COR_MODULO, fontWeight: 700 }}>Δ</td>
                                        {c.delta.map((v, i) => (
                                            <td key={i} style={{
                                                ...tdC, fontWeight: 700,
                                                background: v === 0 ? 'transparent' : v > 0 ? '#dcfce7' : '#fee2e2',
                                                color: v === 0 ? '#cbd5e1' : v > 0 ? '#166534' : '#991b1b',
                                                // A intensidade acompanha o tamanho da mudança.
                                                opacity: v === 0 ? 1 : 0.55 + 0.45 * Math.min(1, Math.abs(v) / (max || 1)),
                                            }}>
                                                {v === 0 ? '·' : v > 0 ? `+${v}` : v}
                                            </td>
                                        ))}</tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const thC = { padding: '4px 3px', background: '#0f172a', fontSize: 10, color: '#fff', fontWeight: 700, textAlign: 'center', minWidth: 26, border: 'none' };
const tdC = { padding: '4px 3px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' };

export default RHDimensionamentoEscalaComparar;
